import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Psychology as PsychologyIcon,
  BarChart as BarChartIcon,
  Security as SecurityIcon,
  Public as PublicIcon,
  Newspaper as NewspaperIcon,
  Computer as ComputerIcon,
  Celebration as CelebrationIcon,
  InsertChart as InsertChartIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationOnIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  School as SchoolIcon,
  Share as ShareIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';

const PublicationStrategiePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fonction pour obtenir le chemin des images
  const getImagePath = (fileName) => {
    if (!fileName || fileName.trim() === '') return null;
    const cleanName = fileName.trim();
    const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.JPG', '.JPEG', '.PNG'];
    const hasExtension = extensions.some(ext => cleanName.endsWith(ext));
    return hasExtension ? `/assets/strategie-ssn/${cleanName}` : `/assets/strategie-ssn/${cleanName}.png`;
  };

  // Données des objectifs stratégiques
  const objectifsStrategie = [
    {
      id: 1,
      titre: "Accessibilité et visibilité",
      description: "Améliorer l'accès et la visibilité des données statistiques auprès des décideurs, des médias, des chercheurs et du grand public.",
      icon: <VisibilityIcon />,
      color: '#1976d2',
    },
    {
      id: 2,
      titre: "Compréhension et appropriation",
      description: "Renforcer la compréhension et l'appropriation des statistiques par tous les utilisateurs à travers des formats vulgarisés et pédagogiques.",
      icon: <PsychologyIcon />,
      color: '#2e7d32',
    },
    {
      id: 3,
      titre: "Culture statistique",
      description: "Développer une culture statistique nationale et promouvoir l'utilisation des données dans la prise de décision publique.",
      icon: <BarChartIcon />,
      color: '#ed6c02',
    },
    {
      id: 4,
      titre: "Crédibilité et confiance",
      description: "Renforcer la crédibilité et la confiance dans les statistiques officielles par une communication transparente et rigoureuse.",
      icon: <SecurityIcon />,
      color: '#9c27b0',
    },
    {
      id: 5,
      titre: "Harmonisation régionale",
      description: "Promouvoir l'alignement du Burundi sur les standards régionaux de la Communauté d'Afrique de l'Est (EAC).",
      icon: <PublicIcon />,
      color: '#008D36',
    },
  ];

  // Données des axes stratégiques
  const axesStrategiques = [
    {
      id: 1,
      titre: "Relations médias",
      description: "Conférences de presse, ateliers de formation des journalistes, Media Breakfast et veille médiatique pour une couverture responsable des statistiques.",
      icon: <NewspaperIcon />,
    },
    {
      id: 2,
      titre: "Communication digitale",
      description: "Refonte du site web, animation des réseaux sociaux, infographies, capsules vidéo et newsletter pour une diffusion moderne et accessible.",
      icon: <ComputerIcon />,
    },
    {
      id: 3,
      titre: "Événements et sensibilisation",
      description: "Mois de la Statistique, dialogues provinciaux, concours étudiants et Statistics Release Events pour rapprocher les données des citoyens.",
      icon: <CelebrationIcon />,
    },
    {
      id: 4,
      titre: "Supports de communication",
      description: "Productions visuelles (infographies, vidéos, témoignages), notes rapides, fiches thématiques et rapport annuel pour une information claire et impactante.",
      icon: <InsertChartIcon />,
    },
  ];

  // Données des phases
  const phases = [
    {
      id: 1,
      periode: "2026-2027",
      titre: "Actions fondamentales",
      description: "Mise en place des outils structurants : refonte du site, charte graphique, base de données journalistes, supports de visibilité, et lancement des activités récurrentes (conférences, infographies, capsules vidéo).",
      activites: "18 mois • 70 000 USD",
    },
    {
      id: 2,
      periode: "2028-2029",
      titre: "Renforcement de l'écosystème",
      description: "Extension aux provinces et universités, développement des relations avec les médias régionaux EAC, production de publications comparatives, veille médiatique et suivi des performances numériques.",
      activites: "24 mois • 93 700 USD",
    },
    {
      id: 3,
      periode: "2030",
      titre: "Innovation et rayonnement régional",
      description: "Consolidation des acquis, lutte contre la désinformation, participation active aux instances EAC, organisation de Statistics Release Events, et évaluation finale de la stratégie.",
      activites: "12 mois • 81 000 USD",
    },
  ];

  const budgetItems = [
    { value: '39', label: 'Activités externes', icon: <ShareIcon /> },
    { value: '17', label: 'Activités internes', icon: <GroupsIcon /> },
    { value: '951 M', label: 'BIF (budget indicatif)', icon: <TrendingUpIcon /> },
    { value: '318 k', label: 'USD (hors formation)', icon: <SchoolIcon /> },
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <NavigationBar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* En-tête */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            mb: 4,
            textAlign: 'center',
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f0f7f0 100%)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip label="INSBU" sx={{ bgcolor: '#E30613', color: 'white', fontWeight: 'bold' }} />
            <Chip label="Système Statistique National (SSN)" sx={{ bgcolor: '#008D36', color: 'white', fontWeight: 'bold' }} />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: '#008D36',
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.5rem', md: '2.2rem' },
              lineHeight: 1.3,
            }}
          >
            Publication de la Stratégie de Communication et de Diffusion des Statistiques du Système Statistique National (SSN) 2026-2030
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, md: 4 }, flexWrap: 'wrap', mb: 3 }}>
            <Chip icon={<CalendarIcon />} label="30 juin 2026" variant="outlined" />
            <Chip icon={<LocationOnIcon />} label="Bujumbura, Burundi" variant="outlined" />
          </Box>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            href="https://api.insbu.bi/storage/documents/01KWBSSTNZGPN8JZPPDM7XPZW3.pdf"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: '#008D36',
              '&:hover': { bgcolor: '#006b29' },
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            Télécharger la stratégie complète (PDF)
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            • 5,2 Mo • 192 pages
          </Typography>
        </Paper>

        {/* Section 1 : Contexte */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#E30613', width: 40, height: 40, mr: 2 }}>
              <DescriptionIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              01 — Contexte et enjeux
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            L'Institut National de la Statistique du Burundi (INSBU) a le plaisir d'annoncer la publication officielle de la <strong>Stratégie de Communication et de Diffusion des Statistiques du Système Statistique National (SSN) du Burundi pour la période 2026-2030</strong>.
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Élaborée dans le cadre du <strong>Projet Régional de Statistiques pour les Résultats de l'Afrique de l'Est au Burundi (PROSTATBU)</strong>, financé par la Banque mondiale, cette stratégie vise à faire de la communication statistique un levier central de transformation du SSN. Elle s'inscrit dans la dynamique de la <strong>Vision « Burundi pays émergent en 2040 et pays développé en 2060 »</strong> et du Plan National de Développement (PND).
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            La stratégie repose sur un principe fondamental : <strong>intégrer la communication à l'ensemble du cycle statistique</strong>, en adoptant une approche centrée sur les utilisateurs et alignée sur les standards régionaux de la Communauté d'Afrique de l'Est (EAC).
          </Typography>

          <Box sx={{ bgcolor: '#e8f5e9', p: 3, borderRadius: 2, borderLeft: '5px solid #008D36' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a5a1a' }}>
              « La communication au cœur du cycle statistique »
            </Typography>
          </Box>
        </Paper>

        {/* Section 2 : Objectifs */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#E30613', width: 40, height: 40, mr: 2 }}>
              <TrendingUpIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              02 — Objectifs stratégiques
            </Typography>
          </Box>

          <Box sx={{ bgcolor: '#f0f8ff', p: 3, borderRadius: 2, borderLeft: '5px solid #E30613', mb: 4 }}>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              <strong>Objectif général :</strong> Renforcer la visibilité, l'accessibilité et l'utilisation des statistiques officielles du Burundi en développant une communication statistique moderne, inclusive et alignée sur les standards régionaux de la Communauté d'Afrique de l'Est (EAC).
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {objectifsStrategie.map((obj) => (
              <Grid item xs={12} md={6} key={obj.id}>
                <Card sx={{ height: '100%', borderLeft: `4px solid ${obj.color}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ bgcolor: obj.color, width: 32, height: 32, mr: 1.5 }}>
                        {obj.icon}
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {obj.titre}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {obj.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Section 3 : Axes stratégiques */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#E30613', width: 40, height: 40, mr: 2 }}>
              <StarIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              03 — Axes stratégiques de communication
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            La stratégie s'articule autour de quatre axes d'intervention majeurs, couvrant l'ensemble des canaux et des publics cibles du SSN.
          </Typography>

          <Grid container spacing={3}>
            {axesStrategiques.map((axe) => (
              <Grid item xs={12} sm={6} key={axe.id}>
                <Card sx={{ textAlign: 'center', height: '100%', p: 2 }}>
                  <Avatar sx={{ bgcolor: '#e8f5e9', color: '#008D36', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                    {axe.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#008D36', mb: 1 }}>
                    {axe.titre}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {axe.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Section 4 : Phases */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#E30613', width: 40, height: 40, mr: 2 }}>
              <CalendarIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              04 — Phases de mise en œuvre
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            La mise en œuvre de la stratégie s'étalera sur cinq ans et se déroulera en trois phases progressives, permettant une montée en puissance adaptée aux capacités institutionnelles du SSN.
          </Typography>

          <Grid container spacing={3}>
            {phases.map((phase) => (
              <Grid item xs={12} md={4} key={phase.id}>
                <Card sx={{ height: '100%', borderTop: `4px solid #008D36` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip label={phase.periode} size="small" sx={{ bgcolor: '#008D36', color: 'white' }} />
                      <Chip label={`Phase ${phase.id}`} size="small" sx={{ bgcolor: '#E30613', color: 'white' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {phase.titre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {phase.description}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ⏱️ {phase.activites}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Section 5 : Budget */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#E30613', width: 40, height: 40, mr: 2 }}>
              <TrendingUpIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              05 — Budget et activités
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            La stratégie prévoit un budget indicatif global de <strong>951 millions BIF</strong> (environ <strong>318 810 USD</strong> sur cinq ans, hors formation), pour la mise en œuvre de <strong>39 activités de communication externe</strong> et <strong>17 activités de communication interne</strong>.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {budgetItems.map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #008D36' }}>
                  <Avatar sx={{ bgcolor: '#e8f5e9', color: '#008D36', mx: 'auto', mb: 1 }}>
                    {item.icon}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#008D36' }}>
                    {item.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, borderLeft: '5px solid #ffc107' }}>
            <Typography variant="body2">
              <strong>Note :</strong> Ce budget est indicatif et sera révisé annuellement en fonction des disponibilités budgétaires et des priorités validées par le Comité de Pilotage du Plan de Communication Statistique (CPPCS).
            </Typography>
          </Box>
        </Paper>

        {/* Section 6 : EAC */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#E30613', width: 40, height: 40, mr: 2 }}>
              <LanguageIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              06 — Alignement régional et coopération EAC
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Conformément aux orientations de la <strong>Stratégie régionale de communication, de plaidoyer et de diffusion des statistiques officielles de la Communauté d'Afrique de l'Est (EAC)</strong>, la stratégie burundaise s'articule autour des piliers régionaux : <em>Advocacy, Dissemination, Statistical Literacy, Partnerships, Innovation, Governance et Monitoring & Evaluation</em>.
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Le Burundi contribuera activement aux initiatives régionales de modernisation statistique, notamment à travers la production de publications comparatives, la participation aux instances EAC et le partage de données sur les plateformes régionales.
          </Typography>

          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Paper
              sx={{
                display: 'inline-block',
                p: 3,
                bgcolor: '#e8f5e9',
                border: '2px dashed #008D36',
                borderRadius: 2,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#008D36' }}>
                🌍 EAC
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Communauté d'Afrique de l'Est
              </Typography>
            </Paper>
          </Box>
        </Paper>

        {/* Section 7 : Remerciements */}
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            bgcolor: '#e8f5e9',
            border: '1px solid #008D36',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#008D36', width: 48, height: 48, mr: 2 }}>
              <CheckCircleIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#008D36' }}>
              Remerciements
            </Typography>
          </Box>

          <Typography variant="body1" align="center" paragraph sx={{ lineHeight: 1.8, maxWidth: 900, mx: 'auto' }}>
            L'INSBU remercie chaleureusement la <strong>Banque mondiale</strong> pour son appui financier à travers le projet PROSTATBU, les <strong>partenaires techniques et financiers</strong> (PNUD, UNFPA, FAO), les <strong>ministères sectoriels</strong>, les <strong>collectivités territoriales</strong>, les <strong>médias</strong>, les <strong>universités</strong>, la <strong>société civile</strong> et l'ensemble des parties prenantes du Système Statistique National pour leur contribution à l'élaboration de cette stratégie.
          </Typography>

          <Typography variant="body1" align="center" sx={{ lineHeight: 1.8, maxWidth: 900, mx: 'auto' }}>
            La validation de cette stratégie marque une étape décisive vers une communication statistique moderne, inclusive et efficace au service du développement du Burundi.
          </Typography>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default PublicationStrategiePage;